import type {
  IconButtonProps,
  MotionPropsFor,
  StackProps,
} from '@hover/blueprint';
import { HStack, IconButton, motion, useDisclosure } from '@hover/blueprint';
import { iChevronLeft, iconEllipsis } from '@hover/icons';
import { useTransform } from 'framer-motion';
import type { RefObject } from 'react';
import React, { useCallback, useState } from 'react';
import { generatePath, useHistory, useRouteMatch } from 'react-router';

import { ProjectShareButton } from 'src/modules/homeownerApp/components/Projects/Project/ProjectShareButton';
import { ConfirmEmbedBackModal } from 'src/modules/homeownerApp/components/TopBar/ConfirmEmbedBackModal';
import { useHomeownerAppContext } from 'src/modules/homeownerApp/hooks/useHomeownerAppContext';
import { Web } from 'src/shared/constants/FeatureFlag';
import type { Project as ProjectType } from 'src/shared/graphql/generated';
import {
  TranslationNamespace,
  useIsEmbed,
  useQueryParam,
  useReactNativeWebView,
  useTranslate,
  useTreatment,
} from 'src/shared/hooks';
import { WebViewScope } from 'src/shared/hooks/useReactNativeWebView';
import { Route } from 'src/shared/route';
import { buttonPressed } from 'src/shared/telemetry';
import { isEnabled } from 'src/shared/telemetry/featureFlag';

import { nudge } from '../../helpers';
import { HomeownerAppMenuNames } from '../../providers/HomeownerAppProvider';
import type { AiOrSampleOriginalImage } from '../../types';
import { isAiOriginal, isSampleOriginal } from '../../types';
import { useNavbarScrollValue } from '../Navbar';
import { StackingHeader } from '../StackingHeader';

const TopBarButton = (props: IconButtonProps) => (
  <IconButton
    iconSize="medium"
    size={{ base: 'small', tablet: 'medium' }}
    variant="ghost"
    {...props}
  />
);

const useTopBarFadeIn = (refElement?: RefObject<HTMLElement>) =>
  useNavbarScrollValue(0, 1, { multiplier: 0.5, offsetElementRef: refElement });

/**
 * Fade an element out as the top bar content fades in
 *
 * @returns opacity motion value
 */
export const useTopBarFadeOut = () =>
  useTransform(useTopBarFadeIn(), v => 1 - v);

export const useIsTopBarVisible = () => {
  const [isTopBarVisible, setIsTopBarVisible] = useState(false);

  useTransform(useTopBarFadeIn(), opacity => opacity > 0.5).on(
    'change',
    setIsTopBarVisible,
  );

  return isTopBarVisible;
};

const MotionHStack = motion(HStack);

export interface TopBarProps extends MotionPropsFor<StackProps> {
  image?: AiOrSampleOriginalImage;
  projectId?: ProjectType['id'];
  project?: boolean;
  showContent?: boolean;
  moreActionsHandler?: () => void;
}

const TopBar = ({
  image,
  projectId,
  project,
  showContent = true,
  moreActionsHandler,
  children,
  ...props
}: TopBarProps) => {
  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);
  const history = useHistory();
  const isEmbed = useIsEmbed();
  const isNewProjectUiEnabled = isEnabled(useTreatment(Web.NEW_PROJECT_UI));
  const returnToProjectId = useQueryParam('returnToProjectId');
  const [isWebView, postMessage] = useReactNativeWebView(
    WebViewScope.MOBILE_HOMEOWNER,
  );
  const routeIsProjectDetails = useRouteMatch(
    Route.HOMEOWNER_APP_PROJECTS_LIST.path,
  );

  const isProjectScreen = routeIsProjectDetails && projectId;
  const { setCurrentMenu, trackingData } = useHomeownerAppContext();

  const aiOriginalImageId = isAiOriginal(image) ? image.id : undefined;

  const handleMoreActions = () => {
    if (!moreActionsHandler)
      setCurrentMenu(HomeownerAppMenuNames.ORIGINAL_IMAGE_MORE_ACTIONS);
    else moreActionsHandler();
  };

  const navigateBack = useCallback(() => {
    if (isWebView) {
      postMessage('handleNativeBack');
    } else if (routeIsProjectDetails) {
      history.push(Route.HOMEOWNER_APP_PROJECTS_LIST.path);
    } else {
      const returnPath =
        isNewProjectUiEnabled && returnToProjectId
          ? generatePath(Route.HOMEOWNER_APP_PROJECT.path, {
              projectId: returnToProjectId,
            })
          : Route.HOMEOWNER_APP_HOME.path;
      history.push(returnPath);
    }
  }, [
    history,
    postMessage,
    isWebView,
    routeIsProjectDetails,
    returnToProjectId,
    isNewProjectUiEnabled,
  ]);

  const confirmEmbedBackModal = useDisclosure();

  const handleBack = () => {
    // We need to check if the AiOriginalImage is present to ensure we do not
    // prompt for confirmation on the "Not Found" page
    if (isEmbed && isAiOriginal(image)) {
      confirmEmbedBackModal.onOpen();
    } else {
      navigateBack();
    }
  };

  const showMoreActionsButton = aiOriginalImageId || project;

  return (
    <StackingHeader
      as="header"
      background="neutral.0"
      data-testid="topbar-header"
      {...props}
    >
      <ConfirmEmbedBackModal
        {...confirmEmbedBackModal}
        onDiscard={navigateBack}
      />
      {showContent && (
        <TopBarButton
          colorScheme={isProjectScreen ? 'light' : undefined}
          data-testid="topbar-back-button"
          icon={iChevronLeft}
          label={t('topBar.back')}
          onClick={() => {
            handleBack();
            buttonPressed({
              ...trackingData,
              button_text: 'left-chevron-icon',
              button_location: 'top-left-main-page',
              track_id: 'space-type-view-back-button',
              ai_original_image_id: aiOriginalImageId,
              output_id: isSampleOriginal(image) ? image.sampleId : undefined,
            });
          }}
          {...nudge({ base: 200, tablet: 400 }, 'left')}
        />
      )}
      <MotionHStack
        flex={1}
        justifyContent="center"
        paddingX={{ base: 200, tablet: 500 }}
      >
        {showContent && children}
      </MotionHStack>
      {isProjectScreen && (
        <ProjectShareButton colorScheme="light" id={projectId} />
      )}
      {showMoreActionsButton && (
        <TopBarButton
          colorScheme={isProjectScreen ? 'light' : undefined}
          icon={iconEllipsis}
          isDisabled={!project && !image}
          label={t('topBar.more')}
          onClick={() => {
            handleMoreActions();
            buttonPressed({
              ...trackingData,
              button_text: 'more-horizontal-icon',
              button_location: 'top-right-main-page',
              track_id: project
                ? 'project-details-overflow-menu'
                : 'space-type-view-overflow-menu',
              ai_original_image_id: aiOriginalImageId,
            });
          }}
          {...nudge({ base: 100, tablet: 300 }, 'right')}
        />
      )}
    </StackingHeader>
  );
};

export { TopBar };
